<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { SourceLegacyChannel, SourceModelParameterDetail } from "@types"
import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"
import { useConfigStore } from "@core/stores/config.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            channel: {
                voie: "VOIE",
                name_label: "Nom de la voie",
                type_label: "Type de variable",
                disabled_label: "Ne pas utiliser cette voie",
                advanced_params: "Paramètres avancés",
                error_duplicate: "Ce type est déjà utilisé sur une autre voie",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

export type SourceLegacyChannelWithDisabled = SourceLegacyChannel & {
    disabled?: boolean
}

interface VariableParameters {
    [variableType: string]: SourceModelParameterDetail[]
}

interface Props {
    /**
     * Identifiant unique du canal
     */
    id?: string
    /**
     * Index du canal (pour l'affichage)
     */
    index?: number
    /**
     * Couleur du canal (pour le badge)
     */
    color?: string
    /**
     * Description du canal
     */
    description?: string
    /**
     * Paramètres par variable
     */
    variableParameters?: VariableParameters
    /**
     * Indique si le channel est en erreur (doublon de type)
     */
    invalid?: boolean
    /**
     * Valeur du channel au format API
     */
    modelValue: SourceLegacyChannelWithDisabled
}

interface Emits {
    /**
     * Émis lors de la mise à jour du channel (format API)
     */
    (e: "update:modelValue", value: SourceLegacyChannelWithDisabled): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    id: "channel",
    index: 0,
    color: undefined,
    variableParameters: () => ({}),
    invalid: false,
    description: undefined,
})

const emit = defineEmits<Emits>()

// ================================
// COMPOSABLES
// ================================
const configStore = useConfigStore()

// ================================
// STATE
// ================================
const isAdvancedOpen = ref<boolean>(false)

// ================================
// COMPUTED
// ================================

/**
 * Style de couleur pour le badge de la voie
 */
const getBadgeStyle = computed(() => {
    const color = props.color
    if (
        color &&
        (/^#[0-9A-F]{6}$/i.test(color) || /^#([0-9A-F]{3}){1,2}$/i.test(color))
    ) {
        return { "--p-badge-primary-background": color }
    }
    return {}
})

/**
 * Type de variable sélectionné (computed avec getter/setter)
 */
const formSelected = computed({
    get: () => props.modelValue?.type || "",
    set: (value: string) => onChangeType(value),
})

/**
 * Nom personnalisé (computed avec getter/setter)
 */
const formName = computed({
    get: () => props.modelValue?.name || "",
    set: (value: string) => onChangeName(value),
})

/**
 * État désactivé (computed avec getter/setter)
 */
const formDisabled = computed({
    get: () => props.modelValue?.disabled || false,
    set: (value: boolean) => onChangeDisabled(value),
})

/**
 * Paramètres disponibles pour la variable sélectionnée
 */
const getSelectedVariableParams = computed((): SourceModelParameterDetail[] => {
    if (
        !formSelected.value ||
        !props.variableParameters?.[formSelected.value]
    ) {
        return []
    }
    return props.variableParameters[formSelected.value]
})

/**
 * Récupère la valeur d'un paramètre depuis modelValue
 */
function getParameterValue(param: SourceModelParameterDetail): number {
    if (param.type === "converter" && props.modelValue.converter) {
        return props.modelValue.converter.a ?? param.default
    }
    if (param.type === "transformer" && props.modelValue.transformer) {
        const transformer = props.modelValue.transformer.find(
            (t) => t.to === param.to
        )
        return transformer?.a ?? param.default
    }
    return param.default
}

/**
 * Récupère le nom lisible d'une variable depuis le store config
 * Si la variable n'est pas trouvée, formate le type en nom lisible
 */
function getVariableName(variableType: string): string {
    if (!variableType) return ""

    // Cherche la variable dans le store config
    const configVariable = configStore.getVariableByType(variableType)
    if (configVariable?.name) {
        return configVariable.name
    }

    // Fallback : formate le type en nom lisible
    // Ex: "temperature_air_interieur" -> "Temperature air interieur"
    return variableType
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Options formatées pour le sélecteur de type
 * Générées à partir des variables disponibles
 */
const getTypeOptions = computed(() => {
    if (
        !props.variableParameters ||
        Object.keys(props.variableParameters).length === 0
    ) {
        // Si pas de variables disponibles, utilise les clés de variableParameters
        return Object.keys(props.variableParameters || {}).map(
            (variableType) => ({
                label: getVariableName(variableType),
                value: variableType,
            })
        )
    }

    return Object.keys(props.variableParameters).map((variableType) => ({
        label: getVariableName(variableType),
        value: variableType,
    }))
})

// ================================
// WATCHERS
// ================================

// ================================
// HANDLERS
// ================================

/**
 * Gère le changement du nom
 */
function onChangeName(value: string): void {
    const updated = { ...props.modelValue, name: value || undefined }
    if (!value) {
        delete updated.name
    }
    emit("update:modelValue", updated)
}

/**
 * Gère le changement du type
 */
function onChangeType(value: string): void {
    const updated: SourceLegacyChannelWithDisabled = {
        ...props.modelValue,
        type: value,
    }
    // Réinitialise converter et transformer lors du changement de type
    delete updated.converter
    delete updated.transformer
    emit("update:modelValue", updated)
}

/**
 * Gère le changement de l'état désactivé
 */
function onChangeDisabled(value: boolean): void {
    const updated = { ...props.modelValue, disabled: value || undefined }
    if (!value) {
        delete updated.disabled
    }
    emit("update:modelValue", updated)
}

/**
 * Gère le changement d'un paramètre
 */
function onChangeParameter(
    param: SourceModelParameterDetail,
    value: number
): void {
    if (!formSelected.value) return

    const updated: SourceLegacyChannelWithDisabled = { ...props.modelValue }

    if (param.type === "converter") {
        updated.converter = {
            name: param.idname,
            a: value,
            b: 0,
        }
    } else if (param.type === "transformer" && param.to) {
        updated.transformer = [
            ...(props.modelValue.transformer || []).filter(
                (t) => t.to !== param.to
            ),
            {
                to: param.to,
                a: value,
                b: 0,
            },
        ]
    }

    emit("update:modelValue", updated)
}

/**
 * Toggle l'affichage des paramètres avancés
 */
function toggleAdvanced(): void {
    isAdvancedOpen.value = !isAdvancedOpen.value
}
</script>

<template>
    <div
        class="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200"
        :class="{ 'opacity-50': formDisabled }"
    >
        <!-- ================================ -->
        <!-- EN-TÊTE DU CANAL -->
        <!-- ================================ -->
        <div class="flex items-center justify-between">
            <!-- Badge de la voie -->
            <BaseBadge :style="getBadgeStyle">
                {{ t("channel.voie") }} {{ index + 1 }}
            </BaseBadge>
            <!-- Description (tooltip) -->
            <BaseIcon
                v-if="description"
                icon="pi pi-info-circle"
                class="text-gray-500"
                v-tooltip="description"
            />
        </div>

        <!-- ================================ -->
        <!-- SÉLECTION DU TYPE -->
        <!-- ================================ -->
        <BaseInputWrapper
            :input-id="`${id}-type`"
            :label="t('channel.type_label')"
            :error="invalid ? t('channel.error_duplicate') : ''"
        >
            <BaseSelect
                :model-value="formSelected"
                :input-id="`${id}-type`"
                :options="getTypeOptions"
                option-label="label"
                option-value="value"
                :disabled="getTypeOptions.length <= 1 || formDisabled"
                :invalid="invalid"
                class="w-full"
                @update:model-value="onChangeType"
            />
        </BaseInputWrapper>

        <!-- ================================ -->
        <!-- PARAMÈTRES AVANCÉS -->
        <!-- ================================ -->
        <div class="flex flex-col gap-3">
            <!-- Bouton toggle -->
            <BaseButton
                severity="secondary"
                text
                @click="toggleAdvanced"
                class="mr-auto w-fit"
                :label="t('channel.advanced_params')"
                :icon="isAdvancedOpen ? 'pi pi-angle-up' : 'pi pi-angle-down'"
                icon-pos="right"
            >
            </BaseButton>

            <!-- Contenu des paramètres avancés -->
            <div
                v-if="isAdvancedOpen"
                class="flex flex-col gap-4 pl-4 border-l-2 border-gray-200"
            >
                <!-- Nom personnalisé -->
                <div v-if="!formDisabled">
                    <BaseInputWrapper
                        :input-id="`${id}-name`"
                        :label="t('channel.name_label')"
                    >
                        <BaseInputText
                            :model-value="formName"
                            :input-id="`${id}-name`"
                            class="w-full"
                            @update:model-value="onChangeName"
                        />
                    </BaseInputWrapper>
                </div>

                <!-- Paramètres de la variable -->
                <div
                    v-if="!formDisabled && getSelectedVariableParams.length > 0"
                    class="flex flex-col gap-3"
                >
                    <div
                        v-for="(param, pIndex) in getSelectedVariableParams"
                        :key="`${id}-param-${pIndex}`"
                        class="flex flex-col gap-1"
                    >
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-medium text-gray-700">
                                {{ param.name }}
                            </span>
                            <span
                                v-if="param.description"
                                class="text-xs text-gray-500"
                                v-tooltip="param.description"
                            >
                                <i class="pi pi-info-circle" />
                            </span>
                        </div>
                        <!-- Input numérique si pas d'options -->
                        <BaseInputNumber
                            v-if="param.options.length === 0"
                            :model-value="
                                formSelected
                                    ? getParameterValue(param)
                                    : param.default
                            "
                            :input-id="`${id}-param-${pIndex}`"
                            :step="0.01"
                            class="w-full"
                            @update:model-value="
                                (value: number) =>
                                    onChangeParameter(param, value)
                            "
                        />
                        <!-- Select si options disponibles -->
                        <BaseSelect
                            v-else
                            :model-value="
                                formSelected
                                    ? getParameterValue(param)
                                    : param.default
                            "
                            :input-id="`${id}-param-${pIndex}`"
                            :options="
                                param.options.map((o) => ({
                                    label: o.text,
                                    value: o.value,
                                }))
                            "
                            option-label="label"
                            option-value="value"
                            class="w-full"
                            @update:model-value="
                                (value: number) =>
                                    onChangeParameter(param, value)
                            "
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col gap-4">
            <BaseDivider class="my-0!" />
            <!-- Checkbox désactiver la voie -->
            <BaseInputWrapper
                :input-id="`${id}-disabled`"
                :label="t('channel.disabled_label')"
                unfloat
                orientation="horizontal"
            >
                <BaseCheckbox
                    :model-value="formDisabled"
                    :input-id="`${id}-disabled`"
                    binary
                    @update:model-value="onChangeDisabled"
                />
            </BaseInputWrapper>
        </div>
    </div>
</template>
